import { useMemo, useState } from 'react';
import { usePaginatedFilter } from '../../../../common/hooks/usePaginatedFilter';
import { useAllotments } from '../../../../hooks/useAllotments';
import { useLots } from '../../../../hooks/useLots';
import {
  useProjectQualityTrackingAll,
  useUpdateQualityTracking,
} from '../../../../hooks/useProjectQualityTracking';
import {
  ITEMS_PER_PAGE,
  INITIAL_COMMENT_STATE,
  QUALITY_COMMENT_STATUS_FILTERS,
} from '../constants/qualityCommentsConstants';
import { filterQualityComments } from '../utils/qualityCommentsUtils';

const normalizeComment = (comment) => ({
  ...INITIAL_COMMENT_STATE,
  ...comment,
  observations: comment?.observations || '',
  control_date: comment?.control_date ? comment.control_date.slice(0, 10) : '',
});

export const useQualityCommentsTable = () => {
  const [selectedAllotment, setSelectedAllotment] = useState('');
  const [selectedLot, setSelectedLot] = useState('');
  const [statusFilter, setStatusFilter] = useState(QUALITY_COMMENT_STATUS_FILTERS.PENDING);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedComment, setSelectedComment] = useState(INITIAL_COMMENT_STATE);

  const { data: allotments = [] } = useAllotments();
  const { data: lots = [] } = useLots({ uuid_allotment: selectedAllotment });
  const { data: comments = [], isLoading } = useProjectQualityTrackingAll();

  const updateMutation = useUpdateQualityTracking();

  const commentsWithObservations = useMemo(
    () =>
      comments
        .filter((comment) => comment?.observations)
        .sort((a, b) => {
          const aTime = new Date(a?.updated_at || 0).getTime();
          const bTime = new Date(b?.updated_at || 0).getTime();
          return bTime - aTime;
        }),
    [comments]
  );

  const {
    data: paginatedData,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: commentsWithObservations,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: (data, term) =>
      filterQualityComments(data, {
        searchTerm: term,
        uuid_allotment: selectedAllotment,
        uuid_lot: selectedLot,
        statusFilter,
      }),
  });

  const handleSelectAllotment = (event) => {
    const value = event.target.value;
    setSelectedAllotment(value);
    setSelectedLot('');
  };

  const handleSelectLot = (event) => {
    setSelectedLot(event.target.value);
  };

  const handleSelectStatusFilter = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleOpenDialog = (comment) => {
    setSelectedComment(normalizeComment(comment));
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedComment((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!selectedComment?.uuid_project || !selectedComment?.uuid_task) {
      return;
    }
    await updateMutation.mutateAsync({
      projectId: selectedComment.uuid_project,
      taskId: selectedComment.uuid_task,
      data: {
        observations: selectedComment.observations,
        control_date: selectedComment.control_date || null,
      },
    });
    handleCloseDialog();
  };

  const handleToggleCompleted = async (comment) => {
    if (!comment?.uuid_project || !comment?.uuid_task) return;
    try {
      await updateMutation.mutateAsync({
        projectId: comment.uuid_project,
        taskId: comment.uuid_task,
        data: {
          status: 'completed',
        },
      });
    } catch (error) {
      console.error('Error updating quality comment status:', error);
    }
  };

  return {
    data: paginatedData,
    isLoading: isLoading || updateMutation.isPending,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    allotments,
    lots,
    selectedAllotment,
    selectedLot,
    statusFilter,
    dialogOpen,
    editMode,
    selectedComment,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleSelectAllotment,
    handleSelectLot,
    handleSelectStatusFilter,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSave,
    handleToggleCompleted,
  };
};
